import retry from "@fullstax/p-retry";

/**
 * Wraps a dynamic `import()` used by `React.lazy`, adding two layers of
 * resilience:
 *
 *  1. A couple of retries for genuinely transient network blips.
 *  2. On a chunk/module-load failure — which almost always means the user is
 *     running a stale build whose chunk hashes no longer exist on the CDN
 *     (i.e. we shipped a new deploy while their tab was open) — a single
 *     hard reload to pick up the fresh `index.html` + new chunk names.
 *
 * The reload is guarded by `sessionStorage` so that a chunk that is genuinely
 * broken (or an offline user) can't trap the page in a reload loop. The guard
 * is cleared on every successful load so a later, unrelated stale-deploy event
 * can still trigger a recovery reload.
 */

const RELOAD_KEY = "lazy-chunk-reload-at";
// Don't reload again if we already reloaded within this window — the new build
// should have loaded by now, so a repeat failure is a real error worth surfacing.
const RELOAD_DEBOUNCE_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    // Chromium / Vite
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // Safari
    /Importing a module script failed/i.test(message) ||
    // Firefox
    /error loading dynamically imported module/i.test(message) ||
    // Webpack-style (defensive)
    /Loading chunk \d+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message)
  );
}

function reloadOnce(): boolean {
  const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  if (Date.now() - lastReload < RELOAD_DEBOUNCE_MS) {
    // We already tried reloading recently and still failed — let the error
    // surface to the boundary instead of looping.
    return false;
  }
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  window.location.reload();
  return true;
}

export function lazyImport<T>(factory: () => Promise<T>): Promise<T> {
  return retry(factory, { retries: 2 })
    .then((module) => {
      sessionStorage.removeItem(RELOAD_KEY);
      return module;
    })
    .catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadOnce()) {
        // Return a promise that never settles so React keeps showing the
        // Suspense fallback (rather than flashing an error boundary) for the
        // brief moment before the page reloads.
        return new Promise<T>(() => {});
      }
      throw error;
    });
}
